import { Box } from '@chakra-ui/react';
import { CROP_MARK_CONFIG, CROP_MARK_WRAPPER_PROPS, PRINT_BLEED_PADDING } from 'constants/printBleed';
import { useAppSelector } from 'modules/Editor/store';
import { selectAllPageBackgroundData, selectPageBackgroundString } from 'modules/Editor/store/infographSelector';
import { selectActivePage } from 'modules/Editor/store/pageSelector';
import { ReactElement, ReactNode, memo } from 'react';
import { isLight } from 'utils/color';
import { TOP_LAYER_Z_INDEX } from 'widgets/sdk';

import { PAGE_TRIM_OVERLAY_SIZE_CSS, PRINT_BLEED_BOX_SHADOW } from './PrintBleed.config';

interface PrintBleedWrapperProps {
  children: ReactNode;
  includePrintBleed?: boolean;
  zoom: number;
}
export const PrintBleedWrapper = memo(({ children, includePrintBleed, zoom }: PrintBleedWrapperProps): ReactElement => {
  const activePageId = useAppSelector(selectActivePage);
  const pageBackground = useAppSelector(selectAllPageBackgroundData(activePageId));
  const pageBackgroundString = useAppSelector(selectPageBackgroundString(activePageId));

  const { background = '', gradientBackground = { colors: [] } } = pageBackground;
  const pageColor = gradientBackground ? gradientBackground.colors[0] || '#fff' : background;
  const lineColor = isLight(pageColor) ? 'var(--vg-colors-gray-700)' : '#fff';

  const PRINT_BLEED_WRAPPER_PROPS = includePrintBleed
    ? {
        mt: '1rem',
        mb: '10',
        boxShadow: PRINT_BLEED_BOX_SHADOW,
        p: `${PRINT_BLEED_PADDING * zoom}px`,
        bg: pageBackgroundString,
        w: 'fit-content',
        position: 'relative' as 'relative',
        overflow: 'visible',
      }
    : {};

  return (
    <Box {...PRINT_BLEED_WRAPPER_PROPS}>
      {children}
      {includePrintBleed && (
        <>
          {/* Page trim overlay */}
          <Box
            position='absolute'
            margin={`${PRINT_BLEED_PADDING * zoom}px`}
            pointerEvents='none'
            w={PAGE_TRIM_OVERLAY_SIZE_CSS(zoom)}
            h={PAGE_TRIM_OVERLAY_SIZE_CSS(zoom)}
            boxShadow={`0 0 0 2px ${lineColor}`}
            opacity='0.5'
            top={0}
            left={0}
            z-index={TOP_LAYER_Z_INDEX}
          />
          {/* Crop Marks */}
          {CROP_MARK_CONFIG.map((cropMark, index) => (
            <Box key={index} {...cropMark} {...CROP_MARK_WRAPPER_PROPS} />
          ))}
        </>
      )}
    </Box>
  );
});

PrintBleedWrapper.displayName = 'PrintBleedWrapper';
